import { useCallback, useEffect, useMemo, useState } from 'react'
import { AlertTriangle, Headphones, RefreshCcw } from 'lucide-react'
import Header from '@/components/layout/Header'
import Panel, {
  PanelEmpty, panelCard, panelChip, panelGhostButton, panelInput,
} from '@/components/layout/Panel'
import EmojiGlyph from '@/components/ui/EmojiGlyph'
import { useLanguage } from '@/contexts/LanguageContext'

/**
 * Dashboard → Yönetim → Briefing.
 *
 * Maç öncesi yoklama: seçilen maçın yayınlanmış kadrosundakiler ve yedekleri
 * Briefing ses odasında mı? Kimin hangi ses kanalında olduğunu backend
 * gateway'den bilir (backend/briefing/router.py); sayfa yalnızca birkaç
 * saniyede bir sorar. Kadrosu yayınlanmamış maçta yoklama yoktur.
 */

interface BriefingMatch {
  event_id: string
  title: string
  start_time: number
  briefing_time: number | null
  map_name: string
  upcoming: boolean
  roster_posted: boolean
}

interface Overview {
  channel_url: string
  synced: boolean
  status: string
  next_event_id: string | null
  in_channel: number
  matches: BriefingMatch[]
}

type PresenceState = 'here' | 'elsewhere' | 'absent'

interface Player {
  user_id: string
  name: string
  avatar_url: string
  state: PresenceState
  channel_name: string
  role_name?: string
  role_emoji?: string
}

interface Check {
  event_id: string
  title: string
  start_time: number | null
  briefing_time: number | null
  roster_posted: boolean
  synced: boolean
  groups: { name: string; kind: 'squad' | 'bench'; players: Player[] }[]
  extras: Player[]
  expected: number
  here: number
}

const POLL_MS = 5000
const FALLBACK_AVATAR = 'https://cdn.discordapp.com/embed/avatars/0.png'

export default function Briefing() {
  const { T, lang } = useLanguage()
  const [overview, setOverview] = useState<Overview | null>(null)
  const [selected, setSelected] = useState<string>('')
  const [check, setCheck] = useState<Check | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [writing, setWriting] = useState(false)
  const [missingOnly, setMissingOnly] = useState(false)

  const locale = lang === 'tr' ? 'tr-TR' : 'en-GB'
  const clock = (ts: number) =>
    new Date(ts * 1000).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })
  const day = (ts: number) =>
    new Date(ts * 1000).toLocaleDateString(locale, { day: 'numeric', month: 'short', weekday: 'short' })

  const loadOverview = useCallback(async () => {
    try {
      const res = await fetch('/api/briefing', { credentials: 'include' })
      if (!res.ok) { setError(T.briefing.loadError); return }
      const data: Overview = await res.json()
      setOverview(data)
      // İlk açılışta sıradaki maç seçili gelir — yoklama çoğu zaman onun için.
      setSelected(cur => cur || data.next_event_id || data.matches[0]?.event_id || '')
    } catch {
      setError(T.briefing.loadError)
    }
  }, [T.briefing.loadError])

  const loadCheck = useCallback(async (eventId: string) => {
    try {
      const res = await fetch(`/api/briefing/${eventId}`, { credentials: 'include' })
      if (!res.ok) { setError(T.briefing.loadError); return }
      setCheck(await res.json())
      setError(null)
    } catch {
      setError(T.briefing.loadError)
    }
  }, [T.briefing.loadError])

  useEffect(() => {
    loadOverview()
    const id = setInterval(loadOverview, POLL_MS * 6)
    return () => clearInterval(id)
  }, [loadOverview])

  useEffect(() => {
    if (!selected) return
    setCheck(prev => (prev?.event_id === selected ? prev : null))
    loadCheck(selected)
    const id = setInterval(() => loadCheck(selected), POLL_MS)
    return () => clearInterval(id)
  }, [selected, loadCheck])

  const rewriteStatus = async () => {
    setWriting(true)
    try {
      const res = await fetch('/api/briefing/status', { method: 'POST', credentials: 'include' })
      if (!res.ok) { setError(T.briefing.statusError); return }
      await loadOverview()
    } catch {
      setError(T.briefing.statusError)
    } finally {
      setWriting(false)
    }
  }

  const upcoming = overview?.matches.filter(m => m.upcoming) ?? []
  const past = overview?.matches.filter(m => !m.upcoming) ?? []

  const groups = useMemo(() => {
    if (!check) return []
    return check.groups
      .map(g => ({ ...g, players: missingOnly ? g.players.filter(p => p.state !== 'here') : g.players }))
      .filter(g => g.players.length > 0)
  }, [check, missingOnly])

  const groupTitle = (g: Check['groups'][number]) =>
    g.kind === 'bench' ? T.briefing.bench : g.name

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <Header title={T.briefing.title} />

      <div className="flex-1 space-y-5 overflow-y-auto p-4 md:p-6">
        <p className="max-w-3xl text-[13px] leading-relaxed text-muted-foreground">{T.briefing.lead}</p>

        {error && (
          <p className="rounded-sm border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </p>
        )}

        {overview && !overview.synced && (
          <p className="flex items-start gap-2 rounded-sm border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-300">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            {T.briefing.notSynced}
          </p>
        )}

        <Panel
          title={T.briefing.channelStatus}
          action={
            <button onClick={rewriteStatus} disabled={writing} className={panelGhostButton}>
              <RefreshCcw className={`h-3.5 w-3.5 ${writing ? 'animate-spin' : ''}`} />
              {T.briefing.refreshStatus}
            </button>
          }
        >
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <p className="text-sm">{overview?.status ?? '…'}</p>
            {overview && (
              <span className="ml-auto flex items-center gap-1.5 text-xs text-muted-foreground">
                <Headphones className="h-3.5 w-3.5" />
                {T.briefing.inChannelNow.replace('{n}', String(overview.in_channel))}
              </span>
            )}
          </div>
        </Panel>

        <Panel
          title={T.briefing.match}
          action={
            overview && overview.matches.length > 0 && (
              <select
                value={selected}
                onChange={e => setSelected(e.target.value)}
                className={`${panelInput} w-auto max-w-[min(28rem,80vw)]`}
              >
                {upcoming.length > 0 && (
                  <optgroup label={T.briefing.upcoming}>
                    {upcoming.map(m => (
                      <option key={m.event_id} value={m.event_id}>
                        {day(m.start_time)} · {m.title}{m.roster_posted ? '' : ` (${T.briefing.noRosterTag})`}
                      </option>
                    ))}
                  </optgroup>
                )}
                {past.length > 0 && (
                  <optgroup label={T.briefing.past}>
                    {past.map(m => (
                      <option key={m.event_id} value={m.event_id}>
                        {day(m.start_time)} · {m.title}{m.roster_posted ? '' : ` (${T.briefing.noRosterTag})`}
                      </option>
                    ))}
                  </optgroup>
                )}
              </select>
            )
          }
        >
          {overview && overview.matches.length === 0 ? (
            <PanelEmpty>{T.briefing.noMatches}</PanelEmpty>
          ) : !check ? (
            <p className="py-6 text-center text-xs text-muted-foreground">…</p>
          ) : !check.roster_posted ? (
            <div className="space-y-3">
              <p className="truncate font-display text-base">{check.title}</p>
              <PanelEmpty>{T.briefing.rosterNotPosted}</PanelEmpty>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                <div className="min-w-0">
                  <p className="truncate font-display text-base">{check.title}</p>
                  <p className="text-xs text-muted-foreground">
                    {check.start_time && day(check.start_time)}
                    {check.briefing_time && ` · ${T.briefing.briefingAt.replace('{time}', clock(check.briefing_time))}`}
                    {check.start_time && ` · ${T.briefing.matchAt.replace('{time}', clock(check.start_time))}`}
                  </p>
                </div>
                <div className="ml-auto flex items-center gap-3">
                  <PresenceMeter here={check.here} total={check.expected} label={T.briefing.presentCount} />
                  <div className="flex gap-1.5">
                    <button className={panelChip(!missingOnly)} onClick={() => setMissingOnly(false)}>
                      {T.briefing.filterAll}
                    </button>
                    <button className={panelChip(missingOnly)} onClick={() => setMissingOnly(true)}>
                      {T.briefing.filterMissing}
                      <span className="ml-1.5 opacity-70">{check.expected - check.here}</span>
                    </button>
                  </div>
                </div>
              </div>

              {check.expected === 0 ? (
                <PanelEmpty>{T.briefing.emptyRoster}</PanelEmpty>
              ) : (
                <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                  {groups.map((g, i) => (
                    <div key={`${g.name}-${i}`} className={`${panelCard} p-3`}>
                      <p className="mb-2 flex items-center justify-between text-sm font-semibold">
                        <span className="truncate">{groupTitle(g)}</span>
                        <span className="text-[11px] font-normal text-muted-foreground">
                          {g.players.filter(p => p.state === 'here').length}/{g.players.length}
                        </span>
                      </p>
                      <ul className="space-y-1">
                        {g.players.map(p => <PlayerRow key={p.user_id} p={p} />)}
                      </ul>
                    </div>
                  ))}
                </div>
              )}

              {check.extras.length > 0 && (
                <div>
                  <p className="mb-2 font-display text-[11px] font-bold uppercase tracking-[0.14em] text-muted-foreground">
                    {T.briefing.extras} · {check.extras.length}
                  </p>
                  <ul className="flex flex-wrap gap-1.5">
                    {check.extras.map(p => (
                      <li key={p.user_id} className={`${panelCard} flex items-center gap-2 px-2 py-1 text-sm`}>
                        <img src={p.avatar_url || FALLBACK_AVATAR} alt="" className="h-5 w-5 rounded-full" />
                        {p.name}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </Panel>
      </div>
    </div>
  )
}

function PresenceMeter({ here, total, label }: { here: number; total: number; label: string }) {
  const pct = total ? Math.round((here / total) * 100) : 0
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-24 overflow-hidden rounded-full bg-white/10">
        <div className="h-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
      </div>
      <span className="whitespace-nowrap text-xs text-muted-foreground">
        {label.replace('{here}', String(here)).replace('{total}', String(total))}
      </span>
    </div>
  )
}

function PlayerRow({ p }: { p: Player }) {
  const { T } = useLanguage()
  const stateText =
    p.state === 'here' ? T.briefing.here
      : p.state === 'elsewhere'
        ? (p.channel_name ? T.briefing.elsewhere.replace('{channel}', p.channel_name) : T.briefing.elsewhereUnknown)
        : T.briefing.absent
  const dot =
    p.state === 'here' ? 'bg-emerald-500'
      : p.state === 'elsewhere' ? 'bg-amber-400'
        : 'bg-primary'
  return (
    <li className={`flex items-center gap-2 rounded-sm px-1.5 py-1 ${p.state === 'here' ? '' : 'bg-white/[0.03]'}`}>
      <span aria-hidden className={`h-2 w-2 shrink-0 rounded-full ${dot}`} />
      <img src={p.avatar_url || FALLBACK_AVATAR} alt="" className="h-6 w-6 shrink-0 rounded-full" />
      <span className="min-w-0 flex-1 truncate text-sm">{p.name}</span>
      {p.role_emoji && <EmojiGlyph emoji={p.role_emoji} size={16} />}
      <span className={`shrink-0 text-[11px] ${p.state === 'here' ? 'text-emerald-400' : 'text-muted-foreground'}`}>
        {stateText}
      </span>
    </li>
  )
}
